import React, { useEffect, useState } from "react";
import { getAllAttendance } from "../../Api/attendanceApi";
import { getAllEmployees, sendReminderApi, updateAttendanceStatusApi } from "../../Api/adminApi";
import toast from "react-hot-toast";
import { Search, Eye, X, Clock, MapPin, Download, TrendingUp, AlertCircle, Camera, Bell } from "lucide-react";
import * as XLSX from "xlsx";
import XLSXStyle from "xlsx-js-style"; // same as xlsx, but keeps cell colours
import { getAllLeavesApi } from "../../Api/leaveApi";
import { saveAs } from "file-saver";

// ✅ Set your backend base URL here
const BASE_URL = "https://dbskillsattendance.iilos.org";

const Attendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [checkInLocationName, setCheckInLocationName] = useState("");
  const [checkOutLocationName, setCheckOutLocationName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [tabFilter, setTabFilter] = useState("all"); // "all", "absent", "pending_checkout"
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ present: 0, absent: 0, total: 0 });
  const [dateFilter, setDateFilter] = useState("today"); 
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const itemsPerPage = 10;
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportType, setExportType] = useState("filtered");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  useEffect(() => {
    fetchAttendanceAndEmployees();
  }, []);

  const getDateString = (date) => {
    return new Date(date).toISOString().split("T")[0];
  };

  const today = getDateString(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = getDateString(yesterdayDate);

  const targetDate = dateFilter === "today" ? today : (dateFilter === "yesterday" ? yesterday : (customRange.from || today));

  useEffect(() => {
    calculateStats(attendance, employees);
  }, [attendance, employees, dateFilter, customRange]);

  const fetchAttendanceAndEmployees = async () => {
    try {
      const [attRes, empRes] = await Promise.all([
        getAllAttendance(),
        getAllEmployees()
      ]);
      if (attRes.success) {
        setAttendance(attRes.data);
      }
      if (empRes.success) {
        const activeEmps = empRes.data.filter(emp => emp.role === "user" && emp.isActive);
        setEmployees(activeEmps);
      }
    } catch (error) {
      console.log("Attendance/Employee fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (attData, empData) => {
    // Target date check
    const presentRecords = attData.filter(item => item.date === targetDate && item.checkIn?.time && item.status !== "Absent");
    const presentCount = presentRecords.length;

    const activeEmps = empData.filter(emp => emp.role === "user" && emp.isActive);
    const totalCount = activeEmps.length;
    
    setStats({
      present: presentCount,
      absent: Math.max(0, totalCount - presentCount),
      total: totalCount,
    });
  };
// ───────── Date-wise report: one row per employee, Check In / Check Out side by side for every date ─────────
const handleRangeExport = async () => {
  if (!exportFrom || !exportTo) {
    toast.error("Please choose the From and To dates");
    return;
  }
  if (exportFrom > exportTo) {
    toast.error("The From date must be before the To date");
    return;
  }
  const loadingToast = toast.loading("Preparing report...");
  try {
    // every date between From and To (inclusive)
    const dates = [];
    for (let d = new Date(`${exportFrom}T00:00:00`); ; d.setDate(d.getDate() + 1)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (iso > exportTo) break;
      dates.push(iso);
      if (dates.length > 93) break;
    }
    if (dates.length > 93) {
      toast.error("Please choose a range of 93 days or less", { id: loadingToast });
      return;
    }

    const todayIST = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    let leaves = [];
    try {
      const r = await getAllLeavesApi();
      if (r && r.success) leaves = r.data || [];
    } catch (e) { /* the report still works without leave information */ }

    // every active employee, plus anyone who has a record in the range
    const inRange = attendance.filter((r) => r.date && r.date >= exportFrom && r.date <= exportTo);
    const people = new Map();
    employees.forEach((e) => people.set(e._id, { id: e._id, name: e.name, designation: e.designation }));
    inRange.forEach((r) => {
      if (r.user?._id && !people.has(r.user._id)) {
        people.set(r.user._id, { id: r.user._id, name: r.user.name, designation: r.user.designation });
      }
    });
    const list = [...people.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    const byKey = {};
    inRange.forEach((r) => { byKey[`${r.user?._id}|${r.date}`] = r; });
    const onLeave = (id, d) =>
      leaves.some((l) => (l.user?._id || l.user) === id && l.status === "Approved" && l.fromDate <= d && l.toDate >= d);
    const timeOf = (t) =>
      t ? new Date(t).toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }) : "";

    // what to show for one employee on one date: [check in cell, check out cell, kind]
    const cellsFor = (p, d) => {
      const rec = byKey[`${p.id}|${d}`];
      const checkedIn = rec && rec.checkIn && rec.checkIn.time;
      if (checkedIn && rec.status !== "Absent") {
        return [timeOf(rec.checkIn.time), rec.checkOut?.time ? timeOf(rec.checkOut.time) : "--", rec.status === "Late" ? "late" : "present"];
      }
      if (onLeave(p.id, d)) return ["Leave", "", "leave"];
      if (checkedIn) return [timeOf(rec.checkIn.time), rec.checkOut?.time ? timeOf(rec.checkOut.time) : "--", "absent-in"]; // checked in but marked Absent
      if (rec && rec.status === "Absent") return ["Absent", "", "absent"];
      if (new Date(`${d}T00:00:00`).getDay() === 0) return ["Week off", "", "off"];
      if (d >= todayIST) return ["", "", "none"];
      return ["Absent", "", "absent"];
    };

    const pretty = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const wd = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short" });

    const title = exportFrom === exportTo ? `Attendance - ${pretty(exportFrom)}` : `Attendance - ${pretty(exportFrom)} to ${pretty(exportTo)}`;
    const head1 = ["S.No", "Employee", "Designation"];
    const head2 = ["", "", ""];
    dates.forEach((d) => { head1.push(`${pretty(d)} (${wd(d)})`, ""); head2.push("Check In", "Check Out"); });
    head1.push("Days Present", "Days Absent", "Days Leave"); head2.push("", "", "");

    const rows = [[title], ["Times are in India time. -- means no check-out yet."], [], head1, head2];
    const kinds = [];
    list.forEach((p, idx) => {
      const row = [idx + 1, p.name, p.designation || ""];
      const k = [];
      let pres = 0, abs = 0, lv = 0;
      dates.forEach((d) => {
        const [i, o, kind] = cellsFor(p, d);
        row.push(i, o); k.push(kind);
        if (kind === "present" || kind === "late") pres++;
        else if (kind === "absent" || kind === "absent-in") abs++;
        else if (kind === "leave") lv++;
      });
      row.push(pres, abs, lv);
      rows.push(row); kinds.push(k);
    });

    const ws = XLSXStyle.utils.aoa_to_sheet(rows);
    const lastCol = 3 + dates.length * 2 + 3 - 1;
    const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Math.min(lastCol, 8) } }, { s: { r: 1, c: 0 }, e: { r: 1, c: Math.min(lastCol, 8) } }];
    [0, 1, 2].forEach((c) => merges.push({ s: { r: 3, c }, e: { r: 4, c } }));
    dates.forEach((_, i) => merges.push({ s: { r: 3, c: 3 + i * 2 }, e: { r: 3, c: 4 + i * 2 } }));
    [0, 1, 2].forEach((n) => merges.push({ s: { r: 3, c: 3 + dates.length * 2 + n }, e: { r: 4, c: 3 + dates.length * 2 + n } }));
    ws["!merges"] = merges;
    ws["!cols"] = [{ wch: 6 }, { wch: 26 }, { wch: 18 }, ...Array.from({ length: dates.length * 2 }, () => ({ wch: 10 })), { wch: 13 }, { wch: 13 }, { wch: 12 }];

    const thin = { style: "thin", color: { rgb: "BFBFBF" } };
    const box = { top: thin, bottom: thin, left: thin, right: thin };
    const centre = { horizontal: "center", vertical: "center", wrapText: true };
    const at = (r, c) => ws[XLSXStyle.utils.encode_cell({ r, c })];
    if (at(0, 0)) at(0, 0).s = { font: { bold: true, sz: 14 } };
    if (at(1, 0)) at(1, 0).s = { font: { italic: true, color: { rgb: "595959" } } };
    for (let r = 3; r <= 4; r++) {
      for (let c = 0; c <= lastCol; c++) {
        const x = at(r, c) || (ws[XLSXStyle.utils.encode_cell({ r, c })] = { t: "s", v: "" });
        x.s = { fill: { fgColor: { rgb: "1F3864" } }, font: { bold: true, color: { rgb: "FFFFFF" } }, alignment: centre, border: box };
      }
    }
    const fills = {
      late: ["FFEB9C", "9C5700"], absent: ["FFC7CE", "9C0006"], "absent-in": ["FFC7CE", "9C0006"],
      leave: ["BDD7EE", "1F4E78"], off: ["E7E6E6", "595959"],
    };
    list.forEach((_, i) => {
      const r = 5 + i;
      for (let c = 0; c <= lastCol; c++) {
        const x = at(r, c) || (ws[XLSXStyle.utils.encode_cell({ r, c })] = { t: "s", v: "" });
        let st = { border: box, alignment: c <= 2 ? { vertical: "center" } : centre };
        if (c >= 3 && c < 3 + dates.length * 2) {
          const f = fills[kinds[i][Math.floor((c - 3) / 2)]];
          if (f) st = { ...st, fill: { fgColor: { rgb: f[0] } }, font: { color: { rgb: f[1] } } };
        } else if (c >= 3 + dates.length * 2) {
          st = { ...st, font: { bold: true } };
        }
        x.s = st;
      }
    });

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Attendance");
    const buf = XLSXStyle.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `Attendance_${exportFrom}${exportFrom === exportTo ? "" : "_to_" + exportTo}.xlsx`
    );
    toast.success("Report downloaded", { id: loadingToast });
    setShowExportDialog(false);
  } catch (err) {
    console.error("Date-wise export error:", err);
    toast.error("Failed to create the report", { id: loadingToast });
  }
};

const handleExport = () => {
  let dataToExport = [];

  if (exportType === "today") {
    const todayStr = new Date().toISOString().split("T")[0];
    dataToExport = attendance.filter((item) => item.date === todayStr);
  } 
  else if (exportType === "filtered") {
    dataToExport = activeList;
  } 
  else {
    dataToExport = attendance;
  }

  // Helper: hours worked as a decimal number (e.g. 8.5)
  const getHoursWorkedDecimal = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    const inTime = new Date(checkIn);
    const outTime = new Date(checkOut);
    const diffMs = outTime - inTime;
    if (diffMs <= 0) return null;
    return diffMs / (1000 * 60 * 60);
  };

  const formattedData = dataToExport.map((item) => {
    const isAbsent = !item.checkIn && !item.user; // If it's an employee object (no checkIn and no populated user field)

    const hoursDecimal = getHoursWorkedDecimal(item.checkIn?.time, item.checkOut?.time);
    const hoursWorkedLabel =
      hoursDecimal === null
        ? "--"
        : `${Math.floor(hoursDecimal)}h ${Math.round((hoursDecimal % 1) * 60)}m`;
    const hoursStatus =
      hoursDecimal === null ? "--" : hoursDecimal >= 8 ? "Completed" : "Incomplete";

    return {
      Name: isAbsent ? item.name : item.user?.name,
      Designation: isAbsent ? item.designation : item.user?.designation,
      Date: isAbsent ? targetDate : item.date,
      CheckIn: item.checkIn?.time
        ? new Date(item.checkIn.time).toLocaleString()
        : "--",
      CheckOut: item.checkOut?.time
        ? new Date(item.checkOut.time).toLocaleString()
        : "--",
      "Hours Worked": hoursWorkedLabel,
      "Hours Status": hoursStatus,
      Status: isAbsent ? "Absent" : (item.checkIn?.time ? "Present" : "Absent"),
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(formattedData);

  // Apply green/red fill to the "Hours Status" column cells only
  const statusColIndex = Object.keys(formattedData[0] || {}).indexOf("Hours Status");
  if (statusColIndex !== -1) {
    formattedData.forEach((row, rowIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: statusColIndex }); // +1 to skip header row
      const cell = worksheet[cellRef];
      if (!cell) return;

      if (row["Hours Status"] === "Completed") {
        cell.s = {
          fill: { fgColor: { rgb: "C6EFCE" } }, // light green
          font: { color: { rgb: "006100" }, bold: true },
        };
      } else if (row["Hours Status"] === "Incomplete") {
        cell.s = {
          fill: { fgColor: { rgb: "FFC7CE" } }, // light red
          font: { color: { rgb: "9C0006" }, bold: true },
        };
      }
    });
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const file = new Blob([excelBuffer], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(file, `Attendance_${Date.now()}.xlsx`);

  setShowExportDialog(false);
};
  const getLocationName = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return data?.display_name || "Address not found";
    } catch {
      return "Location not found";
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371; // Radius of Earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(2); // distance in km
  };

  const handleView = (item) => {
    setSelected(item);
    setCheckInLocationName("");
    setCheckOutLocationName("");

    if (item.checkIn?.location?.latitude && item.checkIn?.location?.longitude) {
      getLocationName(
        item.checkIn.location.latitude,
        item.checkIn.location.longitude
      ).then((addr) => setCheckInLocationName(addr));
    }

    if (item.checkOut?.location?.latitude && item.checkOut?.location?.longitude) {
      getLocationName(
        item.checkOut.location.latitude,
        item.checkOut.location.longitude
      ).then((addr) => setCheckOutLocationName(addr));
    }
  };

  // ✅ Resolves relative paths like /uploads/xxx.jpg to full URLs
  const resolvePhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${BASE_URL}${photo}`;
  };

  // Compute different lists based on tab filter
  const allRecordsList = attendance.filter((item) => {
    let matchDate = true;
    if (dateFilter === "today") {
      matchDate = item.date === today;
    } else if (dateFilter === "yesterday") {
      matchDate = item.date === yesterday;
    } else if (dateFilter === "custom") {
      if (customRange.from && customRange.to) {
        matchDate = item.date >= customRange.from && item.date <= customRange.to;
      }
    }

    const matchesSearch =
      item.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user?.designation?.toLowerCase().includes(searchTerm.toLowerCase());

    // "All Attendance" lists only people who checked in. Absent people (no check-in,
    // or marked Absent) already have their own Absent tab.
    const checkedIn = !!item.checkIn?.time && item.status !== "Absent";

    return matchDate && matchesSearch && checkedIn;
  });

  const absentList = employees.filter((emp) => {
    // Check if there is an attendance record for this user on the targetDate
    const hasRecord = attendance.some(
      (record) => record.user?._id === emp._id && record.date === targetDate && record.checkIn?.time && record.status !== "Absent"
    );

    const matchesSearch =
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.designation?.toLowerCase().includes(searchTerm.toLowerCase());

    return !hasRecord && matchesSearch;
  });

  const pendingCheckoutList = attendance.filter((item) => {
    let matchDate = true;
    if (dateFilter === "today") {
      matchDate = item.date === today;
    } else if (dateFilter === "yesterday") {
      matchDate = item.date === yesterday;
    } else if (dateFilter === "custom") {
      if (customRange.from && customRange.to) {
        matchDate = item.date >= customRange.from && item.date <= customRange.to;
      }
    }

    const matchesSearch =
      item.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user?.designation?.toLowerCase().includes(searchTerm.toLowerCase());

    const isPending = item.checkIn?.time && !item.checkOut?.time;

    return matchDate && matchesSearch && isPending;
  });

  const getActiveList = () => {
    if (tabFilter === "absent") return absentList;
    if (tabFilter === "pending_checkout") return pendingCheckoutList;
    return allRecordsList;
  };

  const activeList = getActiveList();
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentAttendance = activeList.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(activeList.length / itemsPerPage);

  const handleSendReminder = async (userId, type) => {
    const loadingToast = toast.loading("Sending reminder...");
    try {
      const res = await sendReminderApi({ userId, type });
      if (res.success) {
        toast.success(res.message || "Reminder sent successfully!", { id: loadingToast });
      } else {
        toast.error(res.message || "Failed to send reminder.", { id: loadingToast });
      }
    } catch (error) {
      console.error("Failed to send reminder", error);
      toast.error("Failed to send reminder due to connection error.", { id: loadingToast });
    }
  };

  const getStatusBadge = (item) => {
    if (item.status === "Absent") {
      return (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
          <span className="px-3 py-1.5 bg-gradient-to-r from-red-50 to-rose-50 text-red-700 rounded-full text-xs font-semibold border border-red-200">
            Absent{item.checkIn?.time ? " (checked in)" : ""}
          </span>
        </div>
      );
    }
    if (item.checkIn?.time) {
      const isLate = item.status === "Late";
      return (
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${isLate ? "bg-amber-500" : "bg-emerald-500"}`}></div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
            isLate
              ? "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border-amber-200"
              : "bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200"
          }`}>
            {isLate ? "Late" : "Present"}
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
        <span className="px-3 py-1.5 bg-gradient-to-r from-red-50 to-rose-50 text-red-700 rounded-full text-xs font-semibold border border-red-200">
          Absent
        </span>
      </div>
    );
  };

  const handleStatusChange = async (item, status) => {
    if (!item?._id) return;
    try {
      const res = await updateAttendanceStatusApi(item._id, status);
      if (res.success) {
        toast.success(`Status changed to ${status === "OnTime" ? "On Time" : status}`);
        setSelected((prev) => (prev ? { ...prev, status, checkIn: prev.checkIn ? { ...prev.checkIn, status } : prev.checkIn } : prev));
        fetchAttendanceAndEmployees();
      }
    } catch (err) {
      toast.error("Failed to change status");
    }
  };

  const calculateHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "--";
    const inTime = new Date(checkIn);
    const outTime = new Date(checkOut);
    const diffMs = outTime - inTime;
    if (diffMs <= 0) return "--";
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHrs}h ${diffMins}m`;
  };

  // Reusable photo card component used for both check-in and check-out
  const PhotoCard = ({ photo, label, accentColor }) => {
    const src = resolvePhoto(photo);
    const [imgError, setImgError] = useState(false);

    const accent = {
      cyan: {
        border: "border-l-cyan-500",
        bg: "from-cyan-50 to-blue-50",
        icon: "text-cyan-500",
        text: "text-cyan-600",
      },
      purple: {
        border: "border-l-purple-500",
        bg: "from-purple-50 to-pink-50",
        icon: "text-purple-500",
        text: "text-purple-600",
      },
    }[accentColor] || {};

    return (
      <div className={`gradient-card rounded-xl p-4 bg-gradient-to-br ${accent.bg} border-l-4 ${accent.border}`}>
        <div className="flex items-center gap-2 mb-3">
          <Camera size={16} className={accent.icon} />
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
        </div>
        {src && !imgError ? (
          <img
            src={src}
            alt={label}
            className="w-full h-56 object-cover rounded-xl border border-white shadow-md"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-56 flex flex-col items-center justify-center bg-white/60 rounded-xl border border-dashed border-gray-300 text-gray-400 gap-2">
            <Camera size={32} className="opacity-40" />
            <p className="text-sm font-medium">No Photo Available</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        body { font-family: 'Plus+Jakarta+Sans', sans-serif; }

        .gradient-card {
          background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.95) 100%);
          border: 1px solid rgba(99,102,241,0.1);
          backdrop-filter: blur(10px);
        }
        .gradient-text {
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-card { position: relative; overflow: hidden; }
        .stat-card::before {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(6,182,212,0.05) 0%, rgba(99,102,241,0.05) 100%);
          border-radius: 16px;
        }
        .fade-in { animation: fadeIn 0.6s ease-out forwards; opacity: 0; }
        .fade-in-delay-1 { animation-delay: 0.1s; }
        .fade-in-delay-2 { animation-delay: 0.2s; }
        .fade-in-delay-3 { animation-delay: 0.3s; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .table-row-hover { transition: all 0.3s ease; }
        .table-row-hover:hover {
          background: linear-gradient(90deg, rgba(6,182,212,0.08) 0%, transparent 100%);
          transform: translateX(4px);
        }
        .search-input { transition: all 0.3s ease; background: linear-gradient(135deg,#f8fafc,#f1f5f9); }
        .search-input:focus {
          box-shadow: 0 0 20px rgba(6,182,212,0.2), 0 0 40px rgba(99,102,241,0.1);
          background: white;
        }
        .modal-backdrop { animation: backdropFadeIn 0.3s ease-out; }
        @keyframes backdropFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal-content { animation: modalSlideUp 0.4s ease-out; }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pulse-dot { animation: pulseDot 2s cubic-bezier(0.4,0,0.6,1) infinite; }
        @keyframes pulseDot { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
        .button-hover { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .button-hover:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(6,182,212,0.25); }
        .header-gradient { background: linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(99,102,241,0.08) 100%); }
      `}</style>

      {/* Header */}
      <div className="header-gradient border-b border-indigo-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-between items-start">
            <div className="fade-in">
              <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                Attendance
              </h1>
              <p className="text-gray-600 text-lg">Real-time employee presence tracking</p>
            </div>
          <button
  onClick={() => {
    // start the date-wise report from whatever date filter is selected on this page
    const ist = (ms) => new Date(ms).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    const t = ist(Date.now());
    if (dateFilter === "yesterday") { const y = ist(Date.now() - 86400000); setExportFrom(y); setExportTo(y); }
    else if (dateFilter === "custom" && customRange.from && customRange.to) { setExportFrom(customRange.from); setExportTo(customRange.to); }
    else { setExportFrom(t); setExportTo(t); }
    setShowExportDialog(true);
  }}
  className="fade-in-delay-1 button-hover group flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white px-6 py-3 rounded-xl"
>
  <Download size={20} />
  Export Data
</button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-1 border-l-4 border-l-cyan-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">Total Employees</p>
                <p className="text-4xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-2">Active workforce</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-indigo-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-cyan-600" size={28} />
              </div>
            </div>
          </div>
          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-2 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">
                  Present ({dateFilter === "today" ? "Today" : dateFilter === "yesterday" ? "Yesterday" : "Selected"})
                </p>
                <p className="text-4xl font-bold text-emerald-600">{stats.present}</p>
                <p className="text-xs text-gray-400 mt-2">Marked present</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 bg-emerald-500 rounded-full pulse-dot"></div>
              </div>
            </div>
          </div>
          <div className="stat-card gradient-card rounded-2xl p-6 fade-in fade-in-delay-3 border-l-4 border-l-red-500">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider mb-2">
                  Absent ({dateFilter === "today" ? "Today" : dateFilter === "yesterday" ? "Yesterday" : "Selected"})
                </p>
                <p className="text-4xl font-bold text-red-600">{stats.absent}</p>
                <p className="text-xs text-gray-400 mt-2">Not checked in</p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-red-100 to-rose-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="text-red-600" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="gradient-card rounded-2xl p-6 mb-6 fade-in shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or designation..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="search-input w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-300"
              />
            </div>
<div>
  
  {/* Date Filter */}
  <select
    value={dateFilter}
    onChange={(e) => setDateFilter(e.target.value)}
                 className="px-4 py-3.5 border border-gray-200 rounded-xl text-gray-900 font-medium bg-gradient-to-r from-slate-50 to-slate-100 focus:outline-none focus:border-cyan-300"

  >
    <option value="today">Today</option>
    <option value="yesterday">Yesterday</option>
    <option value="custom">Custom Range</option>
  </select>

  {/* Custom From */}
  {dateFilter === "custom" && (
    <input
      type="date"
      value={customRange.from}
      onChange={(e) =>
        setCustomRange({ ...customRange, from: e.target.value })
      }
      className="px-4 py-3 border rounded-xl"
    />
  )}

  {/* Custom To */}
  {dateFilter === "custom" && (
    <input
      type="date"
      value={customRange.to}
      onChange={(e) =>
        setCustomRange({ ...customRange, to: e.target.value })
      }
      className="px-4 py-3 border rounded-xl"
    />
  )}

</div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200 pb-px fade-in">
          {[
            { id: "all", label: `All Attendance (${allRecordsList.length})` },
            { id: "absent", label: `Absent (${absentList.length})` },
            { id: "pending_checkout", label: `Pending Checkout (${pendingCheckoutList.length})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setTabFilter(tab.id);
                setCurrentPage(1);
              }}
              className={`pb-3 px-2 font-semibold text-sm transition-all relative ${
                tabFilter === tab.id
                  ? "text-cyan-600 border-b-2 border-cyan-500"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-4 text-sm text-gray-600 fade-in">
          Showing <span className="font-semibold text-cyan-600">{currentAttendance.length}</span> of{" "}
          <span className="font-semibold text-cyan-600">{activeList.length}</span> {
            tabFilter === "absent" ? "absent employees" : tabFilter === "pending_checkout" ? "pending check-out records" : "records"
          }
        </div>

        {/* Table */}
        {loading ? (
          <div className="gradient-card rounded-2xl p-16 text-center fade-in shadow-sm">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-cyan-500 mx-auto"></div>
              <p className="text-gray-500 mt-4 font-medium">Loading attendance records...</p>
            </div>
          </div>
        ) : (
          <div className="gradient-card rounded-2xl overflow-hidden fade-in shadow-sm">
            <div className="">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-indigo-50 to-cyan-50 border-b border-indigo-100">
                  <tr>
                    {(tabFilter === "absent"
                      ? ["Employee", "Date", "Status", "Action"]
                      : ["Employee", "Date", "Check In", "Check Out", "Hours", "Status", "Action"]
                    ).map((h) => (
                      <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider last:text-center">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentAttendance.length > 0 ? (
                    currentAttendance.map((item, index) => {
                      if (tabFilter === "absent") {
                        return (
                          <tr key={item._id} className="table-row-hover">
                            <td className="px-6 py-4">
                              <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-500">{item.designation}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{targetDate}</td>
                            <td className="px-6 py-4">
                              <span className="px-3 py-1.5 bg-gradient-to-r from-red-50 to-rose-50 text-red-700 rounded-full text-xs font-semibold border border-red-200">
                                Absent
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleSendReminder(item._id, "checkin")}
                                className="button-hover bg-gradient-to-r from-cyan-500 to-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 mx-auto"
                              >
                                <Bell size={12} />
                                Remind Check-in
                              </button>
                            </td>
                          </tr>
                        );
                      } else if (tabFilter === "pending_checkout") {
                        return (
                          <tr key={index} className="table-row-hover">
                            <td className="px-6 py-4">
                              <p className="text-sm font-semibold text-gray-900">{item.user?.name}</p>
                              <p className="text-sm text-gray-500">{item.user?.designation}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.date}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-cyan-500" />
                                <span className="text-sm font-semibold text-gray-900">
                                  {item.checkIn?.time
                                    ? new Date(item.checkIn.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                    : "--"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-400 italic">
                              Working...
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-400 italic">
                              --
                            </td>
                            <td className="px-6 py-4">{getStatusBadge(item)}</td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex justify-center items-center gap-3">
                                <button
                                  onClick={() => handleView(item)}
                                  className="text-cyan-600 hover:text-cyan-700 font-semibold hover:underline transition-colors flex items-center gap-1"
                                >
                                  <Eye size={16} />
                                  View
                                </button>
                                <button
                                  onClick={() => handleSendReminder(item.user?._id, "checkout")}
                                  className="button-hover bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                                >
                                  <Bell size={12} />
                                  Remind Check-out
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      } else {
                        return (
                          <tr key={index} className="table-row-hover">
                            <td className="px-6 py-4">
                              <p className="text-sm font-semibold text-gray-900">{item.user?.name}</p>
                              <p className="text-sm text-gray-500">{item.user?.designation}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.date}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-cyan-500" />
                                <span className="text-sm font-semibold text-gray-900">
                                  {item.checkIn?.time
                                    ? new Date(item.checkIn.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                    : "--"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-purple-500" />
                                <span className="text-sm font-semibold text-gray-900">
                                  {item.checkOut?.time
                                    ? new Date(item.checkOut.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                    : "--"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              {calculateHours(item.checkIn?.time, item.checkOut?.time)}
                            </td>
                            <td className="px-6 py-4">{getStatusBadge(item)}</td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleView(item)}
                                className="text-cyan-600 hover:text-cyan-700 font-semibold hover:underline transition-colors flex items-center justify-center gap-1 mx-auto"
                              >
                                <Eye size={18} />
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    })
                  ) : (
                    <tr>
                      <td colSpan={tabFilter === "absent" ? "4" : "7"} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Search className="text-gray-300 mb-3" size={48} />
                          <p className="text-gray-600 text-lg font-semibold">No records found</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {searchTerm ? "Try adjusting your search" : "No records available"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between fade-in">
            <div className="text-sm text-gray-600">
              Page <span className="font-semibold text-cyan-600">{currentPage}</span> of{" "}
              <span className="font-semibold text-cyan-600">{totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-cyan-500 to-indigo-600 text-white shadow-lg shadow-cyan-500/30"
                          : "border border-gray-200 text-gray-700 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gradient-to-r hover:from-cyan-50 hover:to-indigo-50 hover:border-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== VIEW MODAL ===== */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/25 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => e.target === e.currentTarget && setSelected(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto border border-indigo-100 modal-content">

            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-5 border-b border-indigo-100 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
              <h3 className="text-xl font-bold text-gray-900">Attendance Details</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">

              {/* Employee Info */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Employee Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gradient-card rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Name</p>
                    <p className="text-lg font-bold text-gray-900">{selected.user?.name}</p>
                  </div>
                  <div className="gradient-card rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Designation</p>
                    <p className="text-lg font-bold gradient-text">{selected.user?.designation}</p>
                  </div>
                </div>
              </section>

              {/* Attendance Info */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Attendance Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="gradient-card rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Date</p>
                    <p className="text-lg font-bold text-gray-900">{selected.date}</p>
                  </div>
                  <div className="gradient-card rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Status</p>
                    <div className="mt-2">{getStatusBadge(selected)}</div>
                    {selected._id && (
                      <select
                        value={selected.status || (selected.checkIn?.time ? "OnTime" : "Absent")}
                        onChange={(e) => handleStatusChange(selected, e.target.value)}
                        className="mt-3 w-full text-sm font-semibold border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-cyan-300"
                      >
                        <option value="OnTime">On Time</option>
                        <option value="Late">Late</option>
                        <option value="Absent">Absent</option>
                      </select>
                    )}
                  </div>
                  <div className="gradient-card rounded-xl p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border-l-4 border-l-cyan-500">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Check In</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock size={16} className="text-cyan-600" />
                      <p className="text-lg font-bold text-gray-900">
                        {selected.checkIn?.time
                          ? new Date(selected.checkIn.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : "--"}
                      </p>
                    </div>
                    {selected.checkIn?.status && (
                      <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">
                        {selected.checkIn.status}
                      </span>
                    )}
                  </div>
                  <div className="gradient-card rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-l-purple-500">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Check Out</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock size={16} className="text-purple-600" />
                      <p className="text-lg font-bold text-gray-900">
                        {selected.checkOut?.time
                          ? new Date(selected.checkOut.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : "--"}
                      </p>
                    </div>
                    {selected.checkOut?.status && (
                      <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        {selected.checkOut.status}
                      </span>
                    )}
                  </div>
                  {(selected.checkIn?.time && selected.checkOut?.time) && (
                    <div className="col-span-2 gradient-card rounded-xl p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-l-amber-500">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Total Hours</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {calculateHours(selected.checkIn.time, selected.checkOut.time)}
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Location Info */}
              <section>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Location Information</h4>
                  {selected.checkIn?.location && selected.checkOut?.location && (
                    (() => {
                      const dist = calculateDistance(
                        selected.checkIn.location.latitude,
                        selected.checkIn.location.longitude,
                        selected.checkOut.location.latitude,
                        selected.checkOut.location.longitude
                      );
                      if (dist === null) return null;
                      const isHighDiff = parseFloat(dist) > 0.5; // > 500 meters difference
                      return (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                            isHighDiff
                              ? "bg-amber-100 text-amber-800 border border-amber-300 animate-pulse"
                              : "bg-emerald-100 text-emerald-800 border border-emerald-300"
                          }`}
                        >
                          {isHighDiff ? "⚠️ Location Mismatch Alert: " : "✅ Location Match: "}
                          {dist} km between Check In & Out
                        </span>
                      );
                    })()
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Check In Location Card */}
                  <div className="gradient-card rounded-xl p-4 bg-gradient-to-br from-cyan-50 to-blue-50 border-l-4 border-l-cyan-500">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-cyan-600" />
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Check In Location
                        </p>
                      </div>
                      {selected.checkIn?.location?.latitude && (
                        <a
                          href={`https://www.google.com/maps?q=${selected.checkIn.location.latitude},${selected.checkIn.location.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-bold text-cyan-600 hover:underline flex items-center gap-0.5"
                        >
                          Google Maps ↗
                        </a>
                      )}
                    </div>
                    {selected.checkIn?.location ? (
                      <div>
                        <div className="flex gap-3 text-xs text-gray-500 mb-2">
                          <span>
                            Lat:{" "}
                            <span className="font-semibold text-gray-700">
                              {selected.checkIn.location.latitude}
                            </span>
                          </span>
                          <span>
                            Lng:{" "}
                            <span className="font-semibold text-gray-700">
                              {selected.checkIn.location.longitude}
                            </span>
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 font-medium leading-relaxed">
                          {checkInLocationName || (
                            <span className="flex items-center gap-2 text-gray-400">
                              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-cyan-500 rounded-full animate-spin"></span>
                              Fetching address...
                            </span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Location not captured</p>
                    )}
                  </div>

                  {/* Check Out Location Card */}
                  <div className="gradient-card rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-l-purple-500">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-purple-600" />
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Check Out Location
                        </p>
                      </div>
                      {selected.checkOut?.location?.latitude && (
                        <a
                          href={`https://www.google.com/maps?q=${selected.checkOut.location.latitude},${selected.checkOut.location.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-bold text-purple-600 hover:underline flex items-center gap-0.5"
                        >
                          Google Maps ↗
                        </a>
                      )}
                    </div>
                    {selected.checkOut?.location ? (
                      <div>
                        <div className="flex gap-3 text-xs text-gray-500 mb-2">
                          <span>
                            Lat:{" "}
                            <span className="font-semibold text-gray-700">
                              {selected.checkOut.location.latitude}
                            </span>
                          </span>
                          <span>
                            Lng:{" "}
                            <span className="font-semibold text-gray-700">
                              {selected.checkOut.location.longitude}
                            </span>
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 font-medium leading-relaxed">
                          {checkOutLocationName || (
                            <span className="flex items-center gap-2 text-gray-400">
                              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-purple-500 rounded-full animate-spin"></span>
                              Fetching address...
                            </span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Location not captured</p>
                    )}
                  </div>
                </div>
              </section>

              {/* ✅ Photos Section — Check In & Check Out side by side */}
              <section>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Photos</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PhotoCard
                    photo={selected.checkIn?.photo}
                    label="Check In Photo"
                    accentColor="cyan"
                  />
                  <PhotoCard
                    photo={selected.checkOut?.photo}
                    label="Check Out Photo"
                    accentColor="purple"
                  />
                </div>
              </section>

              {/* Footer */}
              <div className="pt-4 border-t border-indigo-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  Record created:{" "}
                  <span className="font-semibold text-gray-600">
                    {new Date(selected.createdAt).toLocaleString()}
                  </span>
                </p>
                <button
                  onClick={() => setSelected(null)}
                  className="px-6 py-2.5 border border-indigo-200 rounded-lg text-gray-700 font-semibold hover:bg-gradient-to-r hover:from-indigo-50 hover:to-cyan-50 hover:border-cyan-300 transition-all"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
      {showExportDialog && (
  <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl p-6 w-96 shadow-lg">

      <h3 className="text-lg font-bold mb-4">Export Attendance</h3>

      <select
        value={exportType}
        onChange={(e) => setExportType(e.target.value)}
        className="w-full mb-4 px-4 py-2 border rounded-lg"
      >
        <option value="today">Today</option>
        <option value="filtered">Current Filter</option>
        <option value="all">All Data</option>
        <option value="range">Date-wise report (one row per employee)</option>
      </select>

      {exportType === "range" && (
        <div className="mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1">From</label>
              <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-1">To</label>
              <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">Or pick a whole month</label>
            <input
              type="month"
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [y, m] = v.split("-").map(Number);
                const last = new Date(y, m, 0).getDate();
                setExportFrom(`${v}-01`);
                setExportTo(`${v}-${String(last).padStart(2, "0")}`);
              }}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <p className="text-xs text-gray-400">
            One row per employee. For every date you get Check In and Check Out side by side (up to 93 days).
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setShowExportDialog(false)}
          className="px-4 py-2 border rounded-lg"
        >
          Cancel
        </button>

        <button
          onClick={exportType === "range" ? handleRangeExport : handleExport}
          className="px-4 py-2 bg-cyan-600 text-white rounded-lg"
        >
          Download
        </button>
      </div>

    </div>
  </div>
)}
    </div>
  );
};

export default Attendance;